import { Box, Button, VStack } from '@chakra-ui/react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import React, { useMemo } from 'react'

import {
  AutoAttendantNodeData,
  AutoAttendantOption,
} from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import {
  nodeAddOptionButtonStyles,
  NodeCheckbox,
  NodeInput,
  SoundClipSelector,
} from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useAutoAttendantNode } from './hooks/useAutoAttendantNode'

export interface AutoAttendantNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: soundClips is provided via Jotai atoms (soundClipsAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

const AutoAttendantNodeComponent: React.FC<AutoAttendantNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as AutoAttendantNodeData & Record<string, unknown>
  const {
    currentSoundClipId,
    timeout,
    repeatTimes,
    currentAllowExtensionDialing,
    currentOptions,
    digitInputs,
    handleSoundClipChange,
    handleTimeoutChange,
    handleTimeoutBlur,
    handleRepeatTimesChange,
    handleRepeatTimesBlur,
    handleAllowExtensionDialingChange,
    handleDigitChange,
    handleDigitBlur,
    handleOptionsChange,
    hasData,
  } = useAutoAttendantNode(id, nodeData)

  const handleAddOption = React.useCallback(() => {
    // Find the highest existing digit value and increment by 1 (matches legacy behavior)
    let maxDigit = 0
    currentOptions.forEach((option) => {
      const digitValue = parseInt(option.digit || '0', 10)
      if (!isNaN(digitValue) && digitValue > maxDigit) {
        maxDigit = digitValue
      }
    })
    const nextDigit = String(maxDigit + 1)

    const newOption: AutoAttendantOption = {
      digit: nextDigit,
      action: 'transfer',
      target: undefined,
    }
    handleOptionsChange([...currentOptions, newOption])
  }, [currentOptions, handleOptionsChange])

  const handleRemoveOption = React.useCallback(
    (index: number) => {
      const updatedOptions = currentOptions.filter((_, i) => i !== index)
      handleOptionsChange(updatedOptions)
    },
    [currentOptions, handleOptionsChange],
  )

  const t = useSwitchboardT()

  // "On Invalid" is both an input (target) handle at top left AND an output (source) handle at top left
  // "On Timeout" is an output (source) handle at top right
  // Option handles are rendered inline next to each option input (like Call Distribution)
  // Memoize handles array to prevent re-creating on every render
  const topSourceHandles = useMemo(
    () => [
      {
        id: 'onInvalid',
        position: Position.Top as Position,
        style: {
          left: '0%',
          transform: 'translateX(-50%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.transfer.onInvalid', 'On Invalid'),
      },
      {
        id: 'onTimeout',
        position: Position.Top as Position,
        style: {
          left: '100%',
          transform: 'translateX(-50%)',
        } as React.CSSProperties,
        label: text(t, 'switchboard.nodes.transfer.onTimeout', 'On Timeout'),
      },
    ],
    [t],
  )

  return (
    <BaseNode
      label={text(t, 'switchboard.nodeLabels.auto_attendant', 'AUTO ATTENDANT')}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      targetHandlePosition={Position.Left}
      sourceHandlePosition={null}
      additionalSourceHandles={topSourceHandles}
    >
      <VStack align="stretch" gap={2}>
        <SoundClipSelector
          label={text(t, 'switchboard.core.soundClipLabel', 'Sound Clip')}
          value={currentSoundClipId}
          onChange={handleSoundClipChange}
          placeholder={text(
            t,
            'switchboard.core.selectSoundClip',
            'Select sound clip...',
          )}
        />

        <NodeInput
          label={text(t, 'switchboard.nodes.transfer.timeout', 'Timeout')}
          type="number"
          value={timeout}
          onChange={(e) => handleTimeoutChange(e.target.value)}
          onBlur={handleTimeoutBlur}
          placeholder="4"
          min={2}
          max={20}
        />

        <NodeInput
          label={text(
            t,
            'switchboard.nodes.autoAttendant.repeatTimes',
            'Repeat times',
          )}
          type="number"
          value={repeatTimes}
          onChange={(e) => handleRepeatTimesChange(e.target.value)}
          onBlur={handleRepeatTimesBlur}
          placeholder="0"
          min={0}
          max={5}
        />

        <NodeCheckbox
          label={text(
            t,
            'switchboard.nodes.autoAttendant.allowExtensionDialing',
            'Allow Extension Dialing',
          )}
          checked={currentAllowExtensionDialing}
          onChange={(e) => handleAllowExtensionDialingChange(e.target.checked)}
        />

        {/* Options List */}
        {currentOptions.length > 0 && (
          <Box>
            <VStack align="stretch" gap={2}>
              {currentOptions.map((option, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  position="relative"
                >
                  {/* Handle positioned at node edge (like Call Distribution) */}
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`option-${index}`}
                    isConnectableEnd={false}
                    style={{
                      position: 'absolute',
                      right: '-24px', // Account for Card.Body padding (16px) + half handle width (8px)
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      // Colors handled by CSS for hover/highlight support
                    }}
                  />
                  {/* Remove button */}
                  <Button
                    className="nodrag"
                    size="xs"
                    variant="ghost"
                    colorPalette="neutral"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveOption(index)
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    minWidth="auto"
                    width="20px"
                    height="20px"
                    padding={0}
                  >
                    ×
                  </Button>
                  {/* Digit input with label */}
                  <Box flex={1}>
                    <NodeInput
                      label={text(
                        t,
                        'switchboard.nodes.autoAttendant.press',
                        'Press',
                      )}
                      type="number"
                      value={digitInputs[index] || ''}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onBlur={() => handleDigitBlur(index)}
                      placeholder="0-9999"
                      min={0}
                      max={9999}
                      compact
                    />
                  </Box>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Add Option Button */}
        <Box>
          <Button
            className="nodrag"
            {...nodeAddOptionButtonStyles}
            onClick={(e) => {
              e.stopPropagation()
              handleAddOption()
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {text(t, 'switchboard.nodes.common.addOption', '+ Add Option')}
          </Button>
        </Box>
      </VStack>
    </BaseNode>
  )
}

export const AutoAttendantNode = React.memo(
  AutoAttendantNodeComponent,
  nodePropsAreEqual,
)
